//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import * as RadixCheckbox from '@radix-ui/react-checkbox';
import React from 'react';
import styled from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

/**
 * `Checkbox` is a control that allows the user to toggle between checked and not checked.
 *
 * Checkbox is the root component that contains all the parts of a checkbox. An input will
 * also render when used within a form to ensure events propagate correctly.
 *
 * Checkbox.Indicator renders when the checkbox is in a checked state. You can style this
 * element directly, or you can use it as a wrapper to put an icon into, or both.
 *
 * @example
 * <Checkbox
 *   checked={isChecked}
 *   disabled={isDisabled}
 *   id={inputId}
 *   onCheckedChange={(isChecked: boolean) => {
 *     setIsChecked(isChecked);
 *   }}
 * >
 *   <Checkbox.Indicator>
 *     {isChecked ? (
 *       <Checkbox.Icon name='checkmark' />
 *     ) : (
 *       <Checkbox.Icon name='collapse' />
 *     )}
 *   </Checkbox.Indicator>
 * </Checkbox>
 */
export const Checkbox = (
  props: RadixCheckbox.CheckboxProps & ITrackableComponent,
) => <CheckboxRoot {...props} />;

const CheckboxRoot = withTracking(
  styled(RadixCheckbox.Root)(
    ({ theme }) => `
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  
  width: ${theme.spacings[3]};
  height: ${theme.spacings[3]};
  padding: 0;
  border: none;
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px ${theme.colors.gray2};
  
  background-color: ${theme.colors.white};
  
  &:hover {
    box-shadow: inset 0 0 0 1px ${theme.colors.gray7};
  }
  
  &[disabled] {
    cursor: default;
    box-shadow: inset 0 0 0 1px ${theme.colors.gray1};
    background-color: ${theme.colors.gray0};
  }

  &[data-state=checked], &[data-state=indeterminate] {
    box-shadow: none;
    background-color: ${theme.colors.aliases.primary};

    &:hover {
      background-color: ${theme.colors.aliases.primaryShade1};
    }
    
    svg {
      width: ${theme.fontSizes[6]};
    }
    
    &[disabled] {
      background-color: ${theme.colors.gray4};
      color: ${theme.colors.gray0}
    }
  } 
`,
  ),
);

const CheckboxIcon = styled(Icon)(
  ({ theme }) => `
    color: ${theme.colors.white};
    height: ${theme.spacings[3]};
`,
);

const CheckboxIndicator = styled(RadixCheckbox.Indicator)(
  ({ theme }) => `
  display: inline-flex;
  height: ${theme.spacings[3]};  
`,
);

Checkbox.Icon = CheckboxIcon;
Checkbox.Indicator = CheckboxIndicator;
